import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';

import { FHButton } from '../../base';
import { Themes } from '../../../constants';
import { hasRenderableHelperText, mergeSx } from '../utils/general';
import {
  SIGNATURE_BOX_ACTIONS_ROW_SX,
  SIGNATURE_BOX_BUTTONS_SX,
  SIGNATURE_BOX_CANVAS_STYLE,
  SIGNATURE_BOX_CANVAS_STYLE_DISABLED,
  SIGNATURE_BOX_CLEAR_BUTTON_SX,
  SIGNATURE_BOX_HELPER_TEXT_SX,
  SIGNATURE_BOX_MESSAGE_SX,
  SIGNATURE_BOX_PAPER_ERROR_SX,
  SIGNATURE_BOX_PAPER_SX,
  SIGNATURE_BOX_SAVE_BUTTON_SX,
} from './constants';
import { getSignatureImageFitSize } from './utils';
import { FORTA_BLUE } from '../../../utils/colors';

import type { SignatureBoxProps } from './types';

export default function SignatureBox({
  disabled = false,
  readOnly = false,
  value = '',
  height = 180,
  error = false,
  helperText,
  message = 'Signature',
  saveButtonLabel = 'Save signature',
  clearButtonLabel = 'Clear signature',
  onValueChange,
  onSave,
}: SignatureBoxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const signRef = useRef<SignatureCanvas | null>(null);
  const localSignatureRef = useRef('');
  const restoreGenerationRef = useRef(0);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [hasSigned, setHasSigned] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setCanvasWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();

    const container = containerRef.current;
    const resizeObserver =
      container && 'ResizeObserver' in globalThis ? new ResizeObserver(updateWidth) : null;
    if (resizeObserver && container) {
      resizeObserver.observe(container);
    }

    window.addEventListener('resize', updateWidth);
    document.addEventListener('visibilitychange', updateWidth);
    window.addEventListener('focus', updateWidth);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateWidth);
      document.removeEventListener('visibilitychange', updateWidth);
      window.removeEventListener('focus', updateWidth);
    };
  }, []);

  useEffect(() => {
    if (!signRef.current || canvasWidth <= 0) {
      return;
    }

    if (!value) {
      restoreGenerationRef.current += 1;
      localSignatureRef.current = '';
      signRef.current.clear();
      setHasSigned(false);
      return;
    }

    if (localSignatureRef.current && localSignatureRef.current === value) {
      setHasSigned(true);
      return;
    }

    const generation = restoreGenerationRef.current + 1;
    restoreGenerationRef.current = generation;

    const img = new Image();
    img.src = value;
    img.onload = () => {
      if (generation !== restoreGenerationRef.current || !signRef.current || canvasWidth <= 0) {
        return;
      }

      const { width: drawWidth, height: drawHeight } = getSignatureImageFitSize(
        img.width,
        img.height,
        canvasWidth,
        height,
      );

      signRef.current.clear();
      signRef.current.fromDataURL(value, {
        width: drawWidth,
        height: drawHeight,
      });
      localSignatureRef.current = value;
      setHasSigned(true);
    };
  }, [canvasWidth, height, value]);

  const handleClear = useCallback(() => {
    restoreGenerationRef.current += 1;
    localSignatureRef.current = '';
    signRef.current?.clear();
    setHasSigned(false);
    onValueChange?.('');
  }, [onValueChange]);

  const handleEndStroke = useCallback(() => {
    if (!signRef.current || signRef.current.isEmpty()) {
      localSignatureRef.current = '';
      setHasSigned(false);
      return;
    }

    localSignatureRef.current = signRef.current.getTrimmedCanvas().toDataURL('image/png');
    setHasSigned(true);
  }, []);

  const handleSaveSignature = useCallback(async () => {
    if (disabled || !signRef.current || signRef.current.isEmpty()) {
      return;
    }

    const dataUrl = signRef.current.getTrimmedCanvas().toDataURL('image/png');
    setIsSaving(true);
    try {
      await onSave(dataUrl);
      localSignatureRef.current = dataUrl;
    } finally {
      setIsSaving(false);
    }
  }, [disabled, onSave]);

  return (
    <Box>
      <Box ref={containerRef}>
        <Paper
          elevation={0}
          sx={mergeSx(SIGNATURE_BOX_PAPER_SX, error ? SIGNATURE_BOX_PAPER_ERROR_SX : undefined, {
            height,
            cursor: disabled ? 'not-allowed' : 'default',
          })}
        >
          {canvasWidth > 0 ? (
            <SignatureCanvas
              ref={signRef}
              clearOnResize={false}
              canvasProps={{
                className: 'sigCanvas',
                width: canvasWidth,
                height,
                style:
                  disabled || readOnly
                    ? SIGNATURE_BOX_CANVAS_STYLE_DISABLED
                    : SIGNATURE_BOX_CANVAS_STYLE,
              }}
              backgroundColor={FORTA_BLUE.light3}
              onEnd={handleEndStroke}
            />
          ) : null}
        </Paper>
      </Box>
      <Box sx={SIGNATURE_BOX_ACTIONS_ROW_SX}>
        <Typography sx={SIGNATURE_BOX_MESSAGE_SX}>{message}</Typography>
        {hasRenderableHelperText(helperText) ? (
          <Box sx={SIGNATURE_BOX_HELPER_TEXT_SX}>{helperText}</Box>
        ) : null}
        {!readOnly && (
          <Stack direction="row" spacing={2} sx={SIGNATURE_BOX_BUTTONS_SX}>
            <FHButton
              disabled={disabled || isSaving}
              lightBackground
              onClick={handleClear}
              sx={SIGNATURE_BOX_CLEAR_BUTTON_SX}
            >
              {clearButtonLabel}
            </FHButton>
            <FHButton
              colorTheme={Themes.Primary}
              disabled={disabled || isSaving || !hasSigned}
              loading={isSaving}
              onClick={handleSaveSignature}
              sx={SIGNATURE_BOX_SAVE_BUTTON_SX}
            >
              {saveButtonLabel}
            </FHButton>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
